import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../theme-system/ThemeContext';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import ImageLightbox from './ImageLightbox';

// Back to Top Button
const BackToTop = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Back to top"
      style={{
        position: 'fixed',
        bottom: '32px',
        right: '32px',
        width: '44px',
        height: '44px',
        borderRadius: '12px',
        border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
        background: 'var(--bg-secondary, rgba(0,0,0,0.6))',
        backdropFilter: 'blur(12px)',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        zIndex: 999,
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 15l-6-6-6 6"/>
      </svg>
    </button>
  );
};

const Layout = ({ children }) => {
  // The home page is a full-bleed layout; every other page keeps the gutters.
  const { pathname } = useLocation();
  const fullBleed = pathname === '/';
  useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // The sidebar is a drawer over the page now: it starts closed everywhere.
  const toggleSidebar = () => setSidebarOpen((open) => {
    try { localStorage.setItem('sidebar', open ? 'closed' : 'open'); } catch (e) { /* private mode */ }
    return !open;
  });

  // Screen detection - smart responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-close sidebar on mobile when screen resizes
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: '64px'        // the fixed header is exactly 64px
    }}>
      
      {/* Fixed Header */}
      <Header 
        onToggleSidebar={toggleSidebar}
        isMobile={isMobile}
      />

      {/* Main Layout Container */}
      <div style={{
        display: 'flex',
        flex: 1,
        position: 'relative'
      }}>
        {/* Sidebar */}
        <Sidebar 
          isOpen={sidebarOpen}
          isMobile={isMobile}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main Content */}
        <main style={{
          flex: 1,
          minHeight: 'calc(100vh - 64px)',
          padding: fullBleed ? '0' : (isMobile ? '24px 16px 20px' : '36px 60px 40px'),
          maxWidth: '100%',
          // clip (not hidden): still no sideways scroll, but sticky children (the home reel) keep working
          overflowX: 'clip',
          minWidth: 0 // let the flex item shrink when the sidebar is open (hidden used to do this implicitly)
        }}>
          {children}
        </main>

        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div
            style={{
              position: 'fixed',
              top: '64px',
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.32)',
              zIndex: 998
            }}
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>

      {/* Footer */}
      <Footer />

      {/* Back to Top */}
      <BackToTop />

      {/* Image Lightbox */}
      <ImageLightbox />
    </div>
  );
};

export default Layout;